<template>
  <div>
    <div class="page-head">
      <h1>My Attempts</h1>
      <p>Every exam attempt from your 6-attempt, 3-year registration — upcoming and completed.</p>
    </div>
    <dsa-loading v-if="loading" text="Loading attempts…" />
    <template v-else>
      <div v-if="!scheduled.length && !completed.length" class="empty">
        <h3>No attempts yet</h3>
        <p>Register for a subject from the catalog to see attempts here.</p>
        <router-link to="/student/catalog" class="btn btn-outline">Browse subject catalog</router-link>
      </div>

      <div class="section" v-if="scheduled.length">
        <div class="section-head"><h2>Scheduled</h2></div>
        <table>
          <thead>
            <tr>
              <th>Subject</th>
              <th>Window</th>
              <th>Attempt</th>
              <th>Exam Date</th>
              <th>Status</th>
              <th style="text-align:right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in scheduled" :key="row.application_id">
              <td>
                <router-link class="subject" :to="`/student/attempts/${row.subject_code}`">
                  {{ row.subject_name }}
                  <small>{{ row.category }}<template v-if="row.attempt_num > 1"> · Attempt {{ row.attempt_num }}</template></small>
                </router-link>
              </td>
              <td>{{ windowLabel(row) }}</td>
              <td>{{ row.attempt_num }}</td>
              <td>{{ row.exam_date || '—' }}</td>
              <td>
                <span class="chip" :class="isPending(row) ? 'pend' : 'soon'">
                  {{ isPending(row) ? 'Pending' : 'Scheduled' }}
                </span>
              </td>
              <td class="td-actions" style="text-align:right">
                <template v-if="isPending(row)">
                  <button
                    type="button"
                    class="entry"
                    :disabled="redeemingId === row.application_id"
                    @click="redeem(row)"
                  >{{ redeemingId === row.application_id ? 'Redeeming…' : 'Redeem' }}</button>
                </template>
                <template v-else>
                  <button type="button" class="entry" :disabled="examLoadingId === row.application_id" @click="viewExam(row)">
                    {{ examLoadingId === row.application_id ? 'Loading…' : 'View exam' }}
                  </button>
                  <button
                    v-if="hasPrep(row)"
                    type="button"
                    class="entry ext"
                    @click="openPrep(row)"
                  >Prep</button>
                </template>
                <button
                  type="button"
                  class="entry ext"
                  :disabled="cancellingId === row.application_id"
                  @click="cancel(row)"
                >{{ cancellingId === row.application_id ? 'Cancelling…' : 'Cancel' }}</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="section">
        <div class="section-head"><h2>Completed</h2></div>
        <p v-if="completed.length" class="group-note">Score reports are valid for 5 years from the exam date.</p>
        <div v-if="!completed.length" class="empty" style="padding:28px 24px">
          <h3>No completed attempts yet</h3>
          <p style="margin-bottom:0">Completed exams and scores will appear here.</p>
        </div>
        <table v-else>
          <thead>
            <tr>
              <th>Subject</th>
              <th>Score</th>
              <th>Attempt</th>
              <th>Exam Date</th>
              <th style="text-align:right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in completed" :key="row.application_id">
              <td>
                <router-link class="subject" :to="`/student/attempts/${row.subject_code}`">
                  {{ row.subject_name }}<small>{{ row.category }}</small>
                </router-link>
              </td>
              <td>
                <span v-if="scoreView(row.score)" class="score-cell">{{ scoreView(row.score).text }}</span>
                <span v-else class="chip pend">Results pending</span>
              </td>
              <td>{{ row.attempt_num }}</td>
              <td>{{ row.exam_date || '—' }}</td>
              <td class="td-actions" style="text-align:right">
                <template v-if="scoreReleased(row.score)">
                  <button type="button" class="entry" @click="openReport(row.application_id)">View score report</button>
                </template>
                <span v-else class="chip pend">Awaiting release</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <exam-schedule-modal
      :open="examModalOpen"
      :loading="examModalLoading"
      :subject-name="examModalSubject"
      :project-name="examModalProject"
      :sessions="examModalSessions"
      :exam-system-url="examSystemUrl"
      @close="examModalOpen = false"
    />
    <redeem-pass-modal
      :open="redeemModalOpen"
      :step="redeemStep"
      :subject-name="redeemSubjectName"
      :busy="!!redeemingId"
      @close="closeRedeemModal"
      @confirm="confirmRedeem"
      @purchase="goPurchasePass"
    />
  </div>
</template>

<script>
import { getStudentAttemptsGrouped, cancelRegistration, redeemDsaApplicationWithPass } from '@/api/dsa'
import { getUserId } from '@/utils/store'
import { formatScoreDisplay, formatQuarterWindow, openPrepExternal, isPendingPayment } from '@/utils/dsaFormat'
import { loadExamSchedule } from '@/utils/examEntry'
import ExamScheduleModal from '@/components/ExamScheduleModal.vue'
import RedeemPassModal from '@/components/RedeemPassModal.vue'

export default {
  name: 'StudentAttempts',
  components: { ExamScheduleModal, RedeemPassModal },
  data() {
    return {
      loading: true,
      scheduled: [],
      completed: [],
      examLoadingId: '',
      cancellingId: '',
      redeemingId: '',
      redeemModalOpen: false,
      redeemStep: 'confirm',
      redeemSubjectName: '',
      redeemTarget: null,
      examModalOpen: false,
      examModalLoading: false,
      examModalSubject: '',
      examModalProject: '',
      examModalSessions: [],
      examSystemUrl: 'https://exam.learningfirst.io/',
    }
  },
  mounted() {
    this.load()
  },
  methods: {
    scoreView(score) {
      return formatScoreDisplay(score)
    },
    scoreReleased(score) {
      const sv = formatScoreDisplay(score)
      return !!(sv && score && score.visible !== false && !score.pending)
    },
    windowLabel(row) {
      if (!row) return '—'
      if (row.window_label) return row.window_label
      return formatQuarterWindow(row.quarter_code)
    },
    hasPrep(row) {
      return !!(row && (row.has_case_material || (row.prep_url && String(row.prep_url).trim())))
    },
    isPending(row) {
      return isPendingPayment(row)
    },
    openPrep(row) {
      if (!this.hasPrep(row)) return
      openPrepExternal(row.prep_url)
    },
    openReport(application_id) {
      this.$router.push(`/student/transcript/${application_id}`)
    },
    async redeem(row) {
      this.redeemTarget = row
      this.redeemSubjectName = (row && row.subject_name) || 'this subject'
      this.redeemStep = 'confirm'
      this.redeemModalOpen = true
    },
    closeRedeemModal() {
      if (this.redeemingId) return
      this.redeemModalOpen = false
      this.redeemTarget = null
      this.redeemStep = 'confirm'
    },
    goPurchasePass() {
      this.redeemModalOpen = false
      this.redeemTarget = null
      this.redeemStep = 'confirm'
      this.$router.push('/student/pass')
    },
    async confirmRedeem() {
      const row = this.redeemTarget
      if (!row || !row.application_id) return
      this.redeemingId = row.application_id
      try {
        const res = await redeemDsaApplicationWithPass(getUserId(), row.application_id)
        if (res.data && res.data.code === 0) {
          this.$emit('toast', 'Pass redeemed')
          this.redeemModalOpen = false
          this.redeemTarget = null
          this.redeemStep = 'confirm'
          await this.load()
        } else if (res.data && res.data.code === -5) {
          this.redeemStep = 'no_pass'
        } else {
          this.$emit('toast', (res.data && res.data.msg) || 'Redeem failed')
        }
      } catch (e) {
        this.$emit('toast', 'Redeem failed')
      } finally {
        this.redeemingId = ''
      }
    },
    async load() {
      try {
        const res = await getStudentAttemptsGrouped(getUserId())
        const groups = (res.data && res.data.data) || []
        const scheduled = []
        const completed = []
        groups.forEach(g => {
          (g.attempts || []).forEach(a => {
            const row = {
              application_id: a.application_id,
              subject_code: g.subject_code,
              subject_name: g.subject_name || g.subject_code,
              category: a.category || g.category || g.subject_code,
              quarter_code: a.quarter_code,
              window_label: a.window_label,
              exam_date: a.exam_date,
              attempt_num: a.attempt_num,
              prep_url: a.prep_url || '',
              has_case_material: !!a.has_case_material,
              application_status: a.application_status,
              status_label: a.status_label,
              pending_payment: !!a.pending_payment,
              score: a.score,
            }
            // Completed = has score record (even if pending/hidden); scheduled = no score yet
            if (!a.score) scheduled.push(row)
            else completed.push(row)
          })
        })
        this.scheduled = scheduled
        this.completed = completed
      } catch (e) {
        this.$emit('toast', 'Failed to load attempts')
      } finally {
        this.loading = false
      }
    },
    async viewExam(row) {
      if (!row || !row.application_id) return
      this.examLoadingId = row.application_id
      this.examModalOpen = true
      this.examModalLoading = true
      this.examModalSubject = row.subject_name || ''
      this.examModalProject = ''
      this.examModalSessions = []
      try {
        const data = await loadExamSchedule(this, row.application_id)
        if (!data) {
          this.examModalOpen = false
          return
        }
        this.examModalSubject = data.subject_name || this.examModalSubject
        this.examModalProject = data.project_name || ''
        this.examModalSessions = data.exam_sessions || []
        if (data.exam_system_url) this.examSystemUrl = data.exam_system_url
      } finally {
        this.examModalLoading = false
        this.examLoadingId = ''
      }
    },
    async cancel(row) {
      if (!window.confirm(`Cancel registration for ${row.subject_name}? Your Pass attempt will be restored.`)) {
        return
      }
      this.cancellingId = row.application_id
      try {
        const res = await cancelRegistration(row.application_id)
        if (res.data && res.data.code === 0) {
          this.$emit('toast', 'Registration cancelled')
          await this.load()
        } else {
          this.$emit('toast', (res.data && res.data.msg) || 'Cancel failed')
        }
      } catch (e) {
        this.$emit('toast', 'Cancel failed')
      } finally {
        this.cancellingId = ''
      }
    },
  },
}
</script>

<style scoped>
</style>
